import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format } from 'date-fns';
import { DataPoint } from '../../types';
import ExcelDownloadButton from '../../ExcelDownloadButton';
import { AppContext } from '../../../AppContext';
import { isRoleAtOrAbove } from '../../roles';

const colors = ['#8884d8', '#82ca9d', '#FF8042', '#8A2BE2', '#FF7F50'];

const HANOVER_FLEETS_COLOR = '#2B7CD3';

interface ChartCustomer {
  name: string;
  accountNumber?: string | null;
  data: DataPoint[];
}

interface ChartComponentProps {
  data: DataPoint[];
  regionCustomersData: Record<string, ChartCustomer>;
  selectedCustomersData: Record<string, ChartCustomer>;
  title: string;
  startDate: Date;
  endDate: Date;
}

interface CombinedDataPoint {
  date: number;
  collisionRisk?: number | null;
  [key: string]: number | null | undefined;
}

type LegendType =
  | 'line'
  | 'square'
  | 'rect'
  | 'circle'
  | 'cross'
  | 'diamond'
  | 'star'
  | 'triangle'
  | 'wye'
  | 'none';

interface LegendPayload {
  value: string;
  type?: LegendType;
  color: string;
}

interface PercentChangeInfo {
  percentChange: number | null;
  changeDirection: 'increase' | 'decrease' | null;
}

const HANOVER_FLEETS_NAME = 'Hanover Fleets';

const lineDescriptions: Record<string, string> = {
  [HANOVER_FLEETS_NAME]: 'Avg collision risk across all Hanover fleets.',
};

function formatTickLabel(dateNum: number): string {
  return format(new Date(dateNum), 'MMM dd, yyyy');
}

function calculateTicks(minDate: number, maxDate: number, maxTicks: number): number[] {
  const dayMs = 24 * 60 * 60 * 1000;
  const dateRangeInDays = Math.round((maxDate - minDate) / dayMs) + 1;

  const ticks: number[] = [];
  if (dateRangeInDays <= maxTicks) {
    // daily ticks
    for (let i = 0; i < dateRangeInDays; i++) {
      ticks.push(minDate + i * dayMs);
    }
  } else {
    const interval = (maxDate - minDate) / (maxTicks - 1);
    for (let i = 0; i < maxTicks; i++) {
      ticks.push(Math.round(minDate + i * interval));
    }
  }
  return ticks;
}


const ChartComponent: React.FC<ChartComponentProps> = ({
  data,
  regionCustomersData,
  selectedCustomersData,
  title,
  startDate,
  endDate,
}) => {
  const [legendHeight, setLegendHeight] = useState(0);
  const { user } = useContext(AppContext);

  const hasSelections = Object.keys(selectedCustomersData).length > 0;
  const excelCustomersData = hasSelections ? selectedCustomersData : regionCustomersData;
  const canDownloadExcel = user ? isRoleAtOrAbove(user.role_id, 2) : false;

const prepareExcelData = useCallback(() => {
  return data
    .map((dp) => ({
      Date: format(new Date(dp.date), 'yyyy-MM-dd'),
      'Collision Risk': dp.collisionRisk ?? '',
    }))
    .map((baseRow) => {
      const rowCopy = { ...baseRow } as Record<string, string | number>;
      const dateNumber = new Date(baseRow.Date).getTime();

      Object.entries(excelCustomersData).forEach(([_, custObj]) => {
        const match = custObj.data.find((dp) => dp.date === dateNumber);

        const header =
          custObj.accountNumber
            ? `${custObj.name} (${custObj.accountNumber})`
            : custObj.name;

        rowCopy[header] = match?.collisionRisk ?? '';
      });

      return rowCopy;
    });
}, [data, excelCustomersData]);

  const excelData = useMemo(() => prepareExcelData(), [prepareExcelData]);

  const [combinedChartData, setCombinedChartData] = useState<CombinedDataPoint[]>([]);
  useEffect(() => {
    const dateMap: Record<number, CombinedDataPoint> = {};

    data.forEach((pt) => {
      if (!dateMap[pt.date]) {
        dateMap[pt.date] = { date: pt.date };
      }
      dateMap[pt.date].collisionRisk = pt.collisionRisk ?? null;
    });

    Object.entries(selectedCustomersData).forEach(([custId, { data: arr }]) => {
      const dataKey = `customer_${custId}`;
      arr.forEach((pt) => {
        if (!dateMap[pt.date]) {
          dateMap[pt.date] = { date: pt.date };
        }
        dateMap[pt.date][dataKey] = pt.collisionRisk ?? null;
      });
    });

    const selectedCustomerIds = Object.keys(selectedCustomersData);
    if (selectedCustomerIds.length === 1) {
      const custId = selectedCustomerIds[0];
      const customer = selectedCustomersData[custId];
      const collisionDates = customer.data.map((pt) => pt.date);
      const minCollisionDate = Math.min(...collisionDates);
      const maxCollisionDate = Math.max(...collisionDates);

      const engagementData = (customer as any).engagementData;
      if (engagementData && Array.isArray(engagementData)) {
        engagementData.forEach((pt: { date: number; engagementScore: number }) => {
          // Only include engagement data that falls within the collision risk date range
          if (pt.date >= minCollisionDate && pt.date <= maxCollisionDate) {
            if (!dateMap[pt.date]) {
              dateMap[pt.date] = { date: pt.date };
            }
            dateMap[pt.date]['engagementScoreTrend'] = pt.engagementScore;
          }
        });
      }
    }

    const finalArray = Object.values(dateMap)
      // remove any date that has no aggregator or no selected customers
      .filter((entry) => {
        const keys = Object.keys(entry).filter((k) => k !== 'date');
        return keys.some((k) => entry[k] != null);
      })
      .sort((a, b) => a.date - b.date);

    setCombinedChartData(finalArray);
  }, [data, selectedCustomersData]);

  // X-axis ticks
  const minDate = combinedChartData.length
    ? Math.min(...combinedChartData.map((d) => d.date))
    : startDate.getTime();
  const maxDate = combinedChartData.length
    ? Math.max(...combinedChartData.map((d) => d.date))
    : endDate.getTime();

  const dataPointCount = combinedChartData.length;
  const maxTicks = dataPointCount <= 10 ? dataPointCount : 6;
  const ticks = calculateTicks(minDate, maxDate, maxTicks);

  // Build custom Legend payload
  const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>({
    [HANOVER_FLEETS_NAME]: true,
  });

  // Create new chart data that includes a "trend" property
  const chartDataWithTrend = useMemo(() => {
    if (combinedChartData.length < 2) {
      return combinedChartData;
    }

    const n = combinedChartData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    combinedChartData.forEach((pt, i) => {
      const y = pt.collisionRisk ?? 0;
      sumX += i;
      sumY += y;
      sumXY += i * y;
      sumX2 += i * i;
    });
    const slopeAgg = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const interceptAgg = (sumY - slopeAgg * sumX) / n;

    // If exactly one customer is selected, compute their slope/intercept
    const selIds = Object.keys(selectedCustomersData);
    let slopeCust = 0, interceptCust = 0;
    let minCustDate = 0, maxCustDate = 0;
    if (selIds.length === 1) {
      const key = `customer_${selIds[0]}`;
      // find first/last collisionRisk dates for that cust
      const custPts = combinedChartData
        .map((pt, i) => ({ pt, i }))
        .filter(({ pt }) => pt[key] != null);
      if (custPts.length >= 2) {
        minCustDate = custPts[0].pt.date;
        maxCustDate = custPts[custPts.length - 1].pt.date;

        // do the regression
        const m = custPts.length;
        let cx = 0, cy = 0, cxy = 0, cx2 = 0;
        custPts.forEach(({ pt, i }) => {
          const y = pt[key] as number;
          cx += i;
          cy += y;
          cxy += i * y;
          cx2 += i * i;
        });
        slopeCust = (m * cxy - cx * cy) / (m * cx2 - cx * cx);
        interceptCust = (cy - slopeCust * cx) / m;
      }
    }

    return combinedChartData.map((pt, i) => {
      const base: CombinedDataPoint & {
        overallTrend: number;
        trend?: number | null;
      } = {
        ...pt,
        overallTrend: Math.max(0, slopeAgg * i + interceptAgg),
      };

      // only give them a `trend` value if one customer selected
      // and pt.date is inside [minCustDate, maxCustDate]
      if (
        selIds.length === 1 &&
        pt.date >= minCustDate &&
        pt.date <= maxCustDate
      ) {
        base.trend = Math.max(0, slopeCust * i + interceptCust);
      } else {
        base.trend = null;
      }

      return base;
    });
  }, [combinedChartData, selectedCustomersData]);


  const overallTrendData = useMemo(() => {
    const validPoints = combinedChartData.filter((pt) => pt.collisionRisk != null);
    if (validPoints.length < 2) return [];
    const n = validPoints.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    validPoints.forEach((pt) => {
      sumX += pt.date;
      sumY += pt.collisionRisk!;
      sumXY += pt.date * pt.collisionRisk!;
      sumXX += pt.date * pt.date;
    });
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const minDateForTrend = Math.min(...validPoints.map((pt) => pt.date));
    const maxDateForTrend = Math.max(...validPoints.map((pt) => pt.date));
    return [
      { date: minDateForTrend, overallTrend: slope * minDateForTrend + intercept },
      { date: maxDateForTrend, overallTrend: slope * maxDateForTrend + intercept },
    ];
  }, [combinedChartData]);

  // If new customers are selected, ensure they're visible
  useEffect(() => {
    setVisibleLines((prev) => {
      const updated = { ...prev };
      // Ensure aggregator lines are present
      if (!(HANOVER_FLEETS_NAME in updated)) {
        updated[HANOVER_FLEETS_NAME] = true;
      }
      // Add each selected customer line
      Object.values(selectedCustomersData).forEach(({ name }) => {
        if (!(name in updated)) {
          updated[name] = true;
        }
      });
      // If a single customer is selected, add Engagement Score to the toggle list
      if (Object.keys(selectedCustomersData).length === 1 && !('Engagement Score' in updated)) {
        updated['Engagement Score'] = true;
      }
      return updated;
    });
  }, [selectedCustomersData]);

  const legendPayload: LegendPayload[] = useMemo(() => {
    let idx = 0;
    const payload: LegendPayload[] = [
      { value: HANOVER_FLEETS_NAME, type: 'line', color: HANOVER_FLEETS_COLOR },
    ];
    // Add each selected customer's collision risk line
    Object.values(selectedCustomersData).forEach(({ name }) => {
      payload.push({
        value: name,
        type: 'line',
        color: colors[idx % colors.length],
      });
      idx++;
    });
    // If exactly one customer is selected, add the Engagement Score line
    if (Object.keys(selectedCustomersData).length === 1) {
      payload.push({
        value: 'Engagement Score',
        type: 'line',
        color: '#FF0000',
      });
    }
    return payload;
  }, [selectedCustomersData]);

  const handleLegendClick = (legendItem: { value: string }) => {
    const { value } = legendItem;
    setVisibleLines((prev) => ({
      ...prev,
      [value]: !prev[value],
    }));
  };

  // For the tooltip to re-map dataKey => correct displayName
  const customerDataKeys = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(selectedCustomersData).forEach(([custId, { name }]) => {
      map[`customer_${custId}`] = name;
    });
    return map;
  }, [selectedCustomersData]);

  // Percent-change logic
  const [percentChanges, setPercentChanges] = useState<Record<string, PercentChangeInfo>>({});
  useEffect(() => {
    const linesToCheck = [
      { dataKey: 'collisionRisk', name: HANOVER_FLEETS_NAME },
      ...Object.entries(selectedCustomersData).map(([custId, { name }]) => ({
        dataKey: `customer_${custId}`,
        name,
      })),
    ];

    const newChanges: Record<string, PercentChangeInfo> = {};
    linesToCheck.forEach(({ dataKey, name }) => {
      const values = combinedChartData
        .map((pt) => pt[dataKey])
        .filter((val): val is number => val != null);
      if (values.length < 2) {
        newChanges[name] = { percentChange: null, changeDirection: null };
      } else {
        const first = values[0];
        const last = values[values.length - 1];
        const change = last - first;
        const pct = (change / first) * 100;
        let dir: 'increase' | 'decrease' | null = null;
        if (pct > 0) dir = 'increase';
        else if (pct < 0) dir = 'decrease';
        newChanges[name] = {
          percentChange: Math.abs(pct),
          changeDirection: dir,
        };
      }
    });
    setPercentChanges(newChanges);
  }, [combinedChartData, selectedCustomersData]);


  const singleCustomerIds = Object.keys(selectedCustomersData);
  const singleCustomerName =
    singleCustomerIds.length === 1
      ? selectedCustomersData[singleCustomerIds[0]].name
      : '';
  return (
    <Box sx={{ marginTop: 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ marginRight: 1 }}>
            {title}
            <Tooltip
              title="Probability of a collision occurring in the next 100k miles."
              arrow
            >
              <InfoOutlinedIcon color="action" fontSize="inherit" />
            </Tooltip>
          </Typography>
        </Box>

        {canDownloadExcel && (
          <ExcelDownloadButton data={excelData} filename="collision_risk_data.xlsx" />
        )}
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartDataWithTrend}
          margin={{ top: legendHeight, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="date"
            type="number"
            domain={[minDate, maxDate]}
            ticks={ticks}
            tickFormatter={formatTickLabel}
            angle={-45}
            textAnchor="end"
            height={70}
            scale="time"
          />
          <YAxis
            tickFormatter={(val) => `${val}%`}
            label={{
              value: 'Avg Collision Risk',
              angle: -90,
              position: 'insideLeft',
              dx: -10,
              style: { textAnchor: 'middle' },
            }}
          />
          <RechartsTooltip
            labelFormatter={(labelNum) => format(new Date(labelNum), 'PPP')}
            formatter={(value: any, name: string, props: any) => {
              const { dataKey } = props;
              let displayName = customerDataKeys[dataKey] || name;
              if (dataKey === 'collisionRisk') displayName = HANOVER_FLEETS_NAME;
              if (value == null) {
                return ['No data', displayName];
              }
              if (dataKey === 'trend' || dataKey === 'overallTrend') {
                if (dataKey === 'overallTrend') {
                  displayName = "Overall Trend";
                }
                return [`${Number(value).toFixed(2)}%`, displayName];
              }
              const pc = percentChanges[displayName];
              let pctText = '';
              if (pc && pc.percentChange != null) {
                pctText = ` (${pc.changeDirection === 'increase' ? '+' : '-'}${pc.percentChange.toFixed(1)}%)`;
              }
              return [`${value}%`, `${displayName}${pctText}`];
            }}
          />
          <Legend
            wrapperStyle={{ width: '100%', paddingBottom: 20 }}
            verticalAlign="top"
            align="center"
            onClick={handleLegendClick}
            payload={legendPayload}
            onBBoxUpdate={(box) => setLegendHeight((box?.height ?? 0) + 20)}
            formatter={(value) => {
              const active = visibleLines[value];
              const hasDescription = lineDescriptions[value];
              const pc = percentChanges[value];
              let pctText = '';
              if (pc && pc.percentChange != null) {
                pctText = ` (${pc.changeDirection === 'increase' ? '+' : '-'}${pc.percentChange.toFixed(1)}%)`;
              }
              const label = (
                <span
                  style={{
                    textDecoration: active ? 'none' : 'line-through',
                    color: active ? '#000' : '#AAA',
                    cursor: 'pointer',
                  }}
                >
                  {value}{pctText}
                </span>
              );
              return hasDescription ? (
                <Tooltip title={hasDescription} arrow>
                  {label}
                </Tooltip>
              ) : (
                label
              );
            }}
          />

          <Line
            type="monotone"
            dataKey="collisionRisk"
            stroke={HANOVER_FLEETS_COLOR}
            strokeWidth={2}
            dot={false}
            name={HANOVER_FLEETS_NAME}
            hide={!visibleLines[HANOVER_FLEETS_NAME]}
            connectNulls
          />
          {overallTrendData.length > 0 && (
            <Line
              type="monotone"
              dataKey="overallTrend"
              stroke={HANOVER_FLEETS_COLOR}
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 5"
              name="Overall Trend Line"
              hide={!visibleLines[HANOVER_FLEETS_NAME]}
             />
          )}
          {Object.entries(selectedCustomersData).map(([custId, custInfo], index) => {
            const dataKey = `customer_${custId}`;
            return (
              <Line
                key={dataKey}
                type="monotone"
                dataKey={dataKey}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
                name={custInfo.name}
                hide={!visibleLines[custInfo.name]}
                connectNulls
              />
            );
          })}

          {singleCustomerName && (
            <Line
              type="monotone"
              dataKey="trend"
              stroke={colors[0]}
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 5"
              name="Trend Line"
              hide={!visibleLines[singleCustomerName]}
            />
          )}

          {Object.keys(selectedCustomersData).length === 1 && (
            <>
              <YAxis
                yAxisId="engagement"
                orientation="right"
                domain={[0, 100]}
                tickFormatter={(val) => `${val}%`}
                label={{
                  value: 'Engagement Score',
                  angle: 90,
                  position: 'insideRight',
                  dx: 10,
                  style: { textAnchor: 'middle' },
                }}
              />
              <Line
                yAxisId="engagement"
                dataKey="engagementScoreTrend"
                stroke="#FF0000"
                strokeWidth={2}
                dot={false}
                name="Engagement Score"
                connectNulls
                hide={!visibleLines['Engagement Score']}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default React.memo(ChartComponent);
